import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { faStar } from '@fortawesome/pro-regular-svg-icons/faStar';
import { UserPageDataTeamItem } from '@maxpreps/apiserviceclients/apiModels';
import { secureOrigin } from '@maxpreps/network/src/origins';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { SITE_NAV_BREAK_POINT } from '@maxpreps/shared/corecomponents/SiteNav/SiteNavUtils';
import {
  displayToastMessage,
  ToastMode
} from '@maxpreps/shared/corecomponents/Toastify/Toast';
import {
  addFavoriteTeamHelper,
  removeFavoriteTeamHelper
} from '@maxpreps/shared/data/user/favorite-team-data-helpers';
import { getReturnUrl } from '@maxpreps/shared/functions/primitiveHelpers/urlHelpers';
import { getFavoriteTeam } from '@maxpreps/shared/functions/utils/teamUtils';
import { getUserToken } from '@maxpreps/shared/functions/utils/userUtils';
import useHasMounted from '@maxpreps/shared/hooks/useHasMounted';
import { useIsScreenSize } from '@maxpreps/shared/hooks/useIsScreenSize';
import { IWindow } from '@maxpreps/shared/models/IWindow';
import { AsyncDataState } from '@maxpreps/shared/redux/reduxTypes';
import {
  useIsSignedIn,
  useToken,
  useUserFavoriteTeams,
  useUserId,
  useUserStore
} from '@maxpreps/shared/redux/user/userHooks';
import StyledButton, {
  ButtonFill,
  ButtonTheme,
  IButtonProps
} from '@maxpreps/shared/ui/Button/StyledButton';
import Icon from '@maxpreps/shared/ui/Icon/Icon';
import { captureException } from '@sentry/nextjs';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getTrackingTeamName } from '../../utils/getTrackingTeamName';

declare const window: IWindow;

const StyledFollowButton = styled(StyledButton)`
  padding: 8px;
  @media (min-width: ${SITE_NAV_BREAK_POINT}px) {
    padding: 8px 32px;
  }
`;

const getWebFormsMemberUrl = (page, teamId, allSeasonId, season) =>
  `${secureOrigin}/utility/member/${page}?source=react_favorites_team&schoolid=${teamId}&allseasonid=${allSeasonId}&season=${season}&returnurl=${getReturnUrl(
    { favorite: 1 }
  )}`;

const FollowButton = () => {
  const isSignedIn = useIsSignedIn();
  const userId = useUserId();
  const token = useToken();
  const favoriteTeams = useUserFavoriteTeams();
  const userStore = useUserStore();

  const [isLoading, setIsLoading] = useState(false);
  const hasMounted = useHasMounted();
  const isDesktop = useIsScreenSize(SITE_NAV_BREAK_POINT);

  const teamContext = useTeamContext();

  const userDataIsLoading = userStore.pageDataState === AsyncDataState.Loading;

  const { teamId, allSeasonId, season } = teamContext.data;

  const [favoriteTeam, setFavoriteTeam] = useState<UserPageDataTeamItem>();

  useEffect(() => {
    if (!favoriteTeams?.length) return;
    const team = getFavoriteTeam(favoriteTeams, teamContext?.data);
    if (team) setFavoriteTeam(team);
  }, [favoriteTeams, teamContext?.data]);

  // MP-10754
  const toggleFavoriteTeam = useCallback(
    (_token, _userId) => {
      (async () => {
        setIsLoading(true);

        // if they're already a favorite, remove the favorite
        if (favoriteTeam) {
          try {
            await removeFavoriteTeamHelper(
              favoriteTeam.userFavoriteTeamRefId,
              _userId,
              _token
            );
            setFavoriteTeam(undefined);
          } catch (error) {
            displayToastMessage(
              `Unable to unfollow team - ${error}`,
              ToastMode.Error
            );
          }

          setIsLoading(false);

          return;
        }

        // track when a user follows. For now, ignoring when a team is unfolloweed
        if (window?.om?.trackAction) {
          const teamName = getTrackingTeamName(teamContext);
          const data = {
            clickText: `team/${teamName}/follow-button-click`
          };
          window.om.trackAction('team-follow-button-click', data);
        }

        // not a favorite yet, add as a favorite
        try {
          const team = await addFavoriteTeamHelper(
            teamId,
            allSeasonId,
            season,
            _userId,
            _token
          );

          setFavoriteTeam(team);
        } catch (error) {
          displayToastMessage(
            `Unable to follow team - ${error}`,
            ToastMode.Error
          );

          captureException(error);
        }

        setIsLoading(false);
      })();
    },
    [allSeasonId, season, teamContext, teamId, favoriteTeam]
  );

  useEffect(() => {
    if (!isSignedIn || favoriteTeam) return;

    const urlParams = new URLSearchParams(window?.location?.search);

    const favoriteParam = urlParams.get('favorite');
    // If returning from login, automatically call favorites
    if (favoriteParam === '1') {
      toggleFavoriteTeam(token, userId);
    }
  }, [isSignedIn, favoriteTeam, toggleFavoriteTeam, token, userId]);

  // ============================
  // all client side rendering...
  // ============================
  if (!hasMounted) return null;

  const calculatedLoading = isLoading || userDataIsLoading;

  const buttonProps: IButtonProps = !isSignedIn
    ? {
        as: 'a',
        href: getWebFormsMemberUrl('login.aspx', teamId, allSeasonId, season)
      }
    : {
        onClick: () => {
          // if the users token has expired, go get a new one
          if (!getUserToken()) {
            window.location.href = getWebFormsMemberUrl(
              'request_password.aspx',
              teamId,
              allSeasonId,
              season
            );

            return;
          }

          toggleFavoriteTeam(token, userId);
        }
      };

  return (
    // @ts-ignore
    <StyledFollowButton
      disabled={calculatedLoading}
      {...buttonProps}
      $fillMode={favoriteTeam ? ButtonFill.Fill : ButtonFill.Outline}
      $colorScheme={ButtonTheme.White}
    >
      <>
        {calculatedLoading && <Icon icon={faSpinner} spin />}
        {isDesktop ? (
          <span>
            {calculatedLoading
              ? 'UPDATING'
              : favoriteTeam
              ? 'FOLLOWING'
              : 'FOLLOW TEAM'}
          </span>
        ) : (
          <Icon icon={faStar} />
        )}
      </>
    </StyledFollowButton>
  );
};

export default FollowButton;
